import { RoundedBox } from "@react-three/drei";
import React, { useEffect, useMemo } from "react";
import { Color, ShaderMaterial, Vector3 } from "three";
import { useExportContext } from "../../provider/ExportProvider";
import { useZoomChangeContext } from "../../provider/ZoomChangeProvider";
import {
  defaultBoxWithTextBackgroundColor,
  defaultFontColor
} from "../../utils/colors";
import { DEFAULT_ZOOM_VALUE } from "../../utils/constants";
import RENDER_ORDERS from "../../utils/renderOrder";
import CustomTextComponent from "./CustomTextComponent";

export interface RoundedBoxWithTextProps {
  position: Vector3;
  boxSize?: [number, number, number];
  fontSize: number;
  boxColor?: string;
  boxOpacity?: number;
  textColor?: string;
  text: string;
  name?: string;
  radius?: number;
  smoothness?: number;
}

const RoundedBoxWithText: React.FC<RoundedBoxWithTextProps> = ({
  position,
  boxSize = [2.9, 1.2, 0],
  fontSize,
  boxColor = defaultBoxWithTextBackgroundColor,
  boxOpacity = 1.0,
  textColor = defaultFontColor,
  text,
  name = "",
  radius = 0.2,
  smoothness = 4,
}) => {
  const { staggeredZoomData } = useZoomChangeContext();
  const { isExporting, exportCameraWidth, exportCameraHeight } =
    useExportContext();

  const roundedBoxMaterial = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          cameraZoom: { value: 1.0 },
          defaultZoomValue: { value: DEFAULT_ZOOM_VALUE },
          isExporting: { value: false },
          exportWidth: { value: 1.0 },
          exportHeight: { value: 1.0 },
          boxColor: { value: new Color(defaultBoxWithTextBackgroundColor) },
          boxOpacity: { value: 1.0 },
        },
        vertexShader: `
          uniform float cameraZoom;
          uniform float defaultZoomValue;
          uniform bool isExporting;
          uniform float exportWidth;
          uniform float exportHeight;

          void main() {
              vec3 scaledPosition = position;

              if (isExporting) {
                // Export-specific scaling logic
                float baseSize = 50.0;  // Reference size for scaling normalization
                
                // Threshold values
                float minSizeThreshold = 80.0;  // Minimum size threshold
                float midSizeThreshold = 150.0; // Mid-size threshold
                float bigSizeThreshold = 300.0; // Large size threshold
                float hugeSizeThreshold = 450.0; // Huge size threshold
                
                // Scaling factors for each threshold
                float smallScale = 0.8;   // Scaling for very small dimensions
                float midScale = 1.5;     // Scaling for mid-sized dimensions
                float bigScale = 2.0;     // Scaling for large dimensions
                float hugeScale = 2.7;    // Scaling for very large dimensions
                float maxScale = 3.0;     // Maximum scaling factor for huge dimensions
                
                // Ensure dimensions are not too small
                float effectiveHeight = max(exportHeight, minSizeThreshold);
                float effectiveWidth = max(exportWidth, minSizeThreshold);
                
                // Determine the largest dimension
                float largestDimension = max(effectiveHeight, effectiveWidth);
                
                // Initial scaling based on the largest dimension
                float scalingFactor = baseSize / largestDimension;
                
                // Apply logarithmic adjustment for smoother scaling
                scalingFactor = log2(1.0 + scalingFactor);
                
                // Clamp scaling factor to prevent extremes
                float lowClamp = 0.2;  // Lower bound for scaling
                float highClamp = 5.0; // Upper bound for scaling
                scalingFactor = clamp(scalingFactor, lowClamp, highClamp);
                
                // Handle specific thresholds with exact matching
                if (largestDimension > hugeSizeThreshold) {
                    scalingFactor = max(scalingFactor, maxScale);
                } else if (largestDimension > bigSizeThreshold) {
                    scalingFactor = max(scalingFactor, hugeScale);
                } else if (largestDimension > midSizeThreshold) {
                    scalingFactor = max(scalingFactor, bigScale);
                } else if (largestDimension > minSizeThreshold) {
                    scalingFactor = max(scalingFactor, midScale);
                } else {
                    scalingFactor = max(scalingFactor, smallScale);
                }
                
                // Apply scaling to position
                scaledPosition.xy *= scalingFactor;
              } else {
                  float scaleFactor = defaultZoomValue / cameraZoom;
                  float high = 3.0;
                  float low = 0.3;
                  scaleFactor = clamp(scaleFactor, low, high);
                  scaledPosition.xy *= scaleFactor;
              }

              gl_Position = projectionMatrix * modelViewMatrix * vec4(scaledPosition, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 boxColor;
          uniform float boxOpacity;

          void main() {
            gl_FragColor = vec4(boxColor, boxOpacity);
          }
        `,
        transparent: true,
        depthWrite: true,
        depthTest: true,
      }),
    []
  );

  useEffect(() => {
    if (roundedBoxMaterial) {
      roundedBoxMaterial.uniforms.cameraZoom.value = staggeredZoomData || 1.0;
      roundedBoxMaterial.uniforms.isExporting.value = isExporting;
      roundedBoxMaterial.uniforms.exportWidth.value = exportCameraWidth;
      roundedBoxMaterial.uniforms.exportHeight.value = exportCameraHeight;
      roundedBoxMaterial.uniforms.boxColor.value.set(boxColor);
      roundedBoxMaterial.uniforms.boxOpacity.value = boxOpacity;
    }
  }, [
    staggeredZoomData,
    isExporting,
    exportCameraWidth,
    exportCameraHeight,
    boxColor,
    boxOpacity,
    roundedBoxMaterial,
  ]);

  useEffect(() => () => {
    roundedBoxMaterial.dispose();
  }, [roundedBoxMaterial]);

  return (
    <group name={`group-roundedBoxWithText-${text}`} position={position}>
      <RoundedBox
        args={boxSize}
        radius={radius}
        smoothness={smoothness}
        name={name === "" ? `RoundedBoxWithText-"${text}"-box` : `${name}-box`}
        renderOrder={RENDER_ORDERS.roundedBox}
      >
        <primitive attach="material" object={roundedBoxMaterial} />
      </RoundedBox>

      <CustomTextComponent
        text={text}
        fontSize={fontSize}
        color={textColor}
        staggeredZoomData={staggeredZoomData}
      />
    </group>
  );
};

export default RoundedBoxWithText;
